<template lang="pug">
    v-form(
        ref="form"
        :disabled="!validCustomer"
    )
        v-alert(dense outlined type='error' v-if="!validDispositionActions") No disposition actions found.
        v-container
            template(v-for="action in sortedDispositionActions")
                //- Employee type
                AutocompleteDividedByType(
                    v-if="action.actionable_type === 'employee'"
                    :key="action.disposition_action_id"
                    :items="employees"
                    v-model="formEmployee[action.disposition_action_id]"
                    item-divider-variable="department_name"
                    :label="getActionLabel(action)"
                    item-text="display_name"
                    item-value="employee_id"
                    :multiple="action.is_multi_select"
                    :chips="action.is_multi_select"
                    :clearable="!action.is_multi_select"
                    :deletable-chips="action.is_multi_select"
                    :auto-close-on-select="!action.is_multi_select"
                    :loading="isLoadingEmployees"
                    :rules="getRulesByAction(action)"
                )

                //- Customer vehicle type
                v-autocomplete(
                    v-if="action.actionable_type === 'customer_vehicle'"
                    :key="action.disposition_action_id"
                    :items="customerVehiclesWithDescription"
                    v-model="formCustomerVehicle[action.disposition_action_id]"
                    :label="getActionLabel(action)"
                    item-text="vehicle_description"
                    item-value="customer_vehicle_id"
                    :clearable="!action.is_multi_select"
                    :multiple="action.is_multi_select"
                    :chips="action.is_multi_select"
                    :deletable-chips="action.is_multi_select"
                    :auto-close-on-select="!action.is_multi_select"
                    :rules="getRulesByAction(action)"
                )

                //- Disposition type
                v-select(
                    v-if="action.actionable_type === 'disposition'"
                    :key="action.disposition_action_id"
                    v-model="formDispositions[action.disposition_action_id]"
                    :label="getActionLabel(action)"
                    :items="filteredCampaignDispositions"
                    item-text="disposition_description"
                    item-value="disposition_id"
                    :rules="getRulesByAction(action)"
                    clearable
                )

                //- Input type
                v-text-field(
                    v-if="action.actionable_type === 'input'"
                    :key="action.disposition_action_id"
                    :ref="'actionInput' + action.disposition_action_id"
                    v-model="formInput[action.disposition_action_id]"
                    :label="getActionLabel(action)"
                    placeholder="Enter text..."
                    :rules="getRulesByAction(action)"
                    :append-icon="getValidationIcon(action)"
                )

                //- Textarea type
                v-textarea(
                    v-if="action.actionable_type === 'textarea'"
                    :key="action.disposition_action_id"
                    v-model="formTextarea[action.disposition_action_id]"
                    :label="getActionLabel(action)"
                    placeholder="Enter text..."
                    :rules="getRulesByAction(action)"
                    solo
                )

                //- Multiple choice type
                v-select(
                    v-if="action.actionable_type === 'multiple_choice'"
                    :key="action.disposition_action_id"
                    v-model="formMultipleChoice[action.disposition_action_id]"
                    :label="getActionLabel(action)"
                    :items="action.disposition_action_multiple_choice_options"
                    item-text="action_option"
                    item-value="disposition_action_multiple_choice_option_id"
                    :multiple="action.is_multi_select"
                    :chips="action.is_multi_select"
                    :deletable-chips="action.is_multi_select"
                    :auto-close-on-select="!action.is_multi_select"
                    :rules="getRulesByAction(action)"
                    clearable
                )

</template>

<script lang="ts">
import Vue from 'vue';
// Components
import AutocompleteDividedByType from '@/components/shared/autocomplete_divided_by_type.vue';
//Data
import {
    GetEmployees,
    GetCustomerVehicles
} from "@/data";

//helpers
import { titleize } from "@/helpers/general";

import { hasValidEmail } from "@/helpers/email";
import { cleanupPhoneNumber } from "@/helpers/phone_number";

export default Vue.extend({
    data() {
        return {
            actionsWithInputType: [],
            actionsWithCustomerVehicleType: [],
            actionsWithEmployeeType: [],
            actionsWithDispositionType: [],
            actionsWithTextareaType: [],
            actionsWithMultipleChoiceType: [],
            customerVehicles: [],
            employees: [],
            isLoadingEmployees: false,
            formEmployee: {},
            formInput: {},
            formTextarea: {},
            formMultipleChoice: {},
            formCustomerVehicle: {},
            formDispositions: {},
        }
    },
    methods: {
        emitDispositionActionValues() {
            this.$emit('disposition-action-values', this.dispositionActionValues);
        },
        getEmployees() {
            if (!(this.organizationId > 0)) {
                return;
            }

            const options = {
                organization_id: this.organizationId,
                is_disposition_actionable: true,
                exclude_click_to_call_employees: true,
            }

            this.isLoadingEmployees = true;

            GetEmployees(options).then((response) => {
                if (response?.Data?.employees?.length > 0) {
                    this.employees = response.Data.employees;
                    this.employeeTypes = this.employees.map(employee => employee.employee_type);
                }
                this.isLoadingEmployees = false;
            });
        },
        getDispositionActionId(actionableType) {
            const action = this.dispositionActions.find(action => action.actionable_type === actionableType);
            return action?.disposition_action_id || 0;
        },
        getRulesByAction(action) {
            if (!action) {
                return [];
            }

            const rules = [];

            if (action.is_required) {
                rules.push(
                    v => !!v || 'Required',
                );
            }

            if (action.actionable_type === 'input') {
                switch (action.validation_type) {
                case 'email_address':
                    rules.push(
                        v => !v || hasValidEmail(v) || 'Input must be a valid email address',
                    );
                    break;
                case 'phone_number':
                    // TODO: Add support for other countries?
                    rules.push(
                        v => !v || cleanupPhoneNumber(v, 'US').valid || 'Input must be a valid phone number',
                    );
                    break;
                case 'vin':
                    rules.push(
                        v => !v || v.match(/^\d{17}$/) || 'Input must be a valid VIN',
                    );
                    break;
                case 'number':
                    rules.push(
                        v => !v || v.match(/^\d+$/) || 'Input must be a number',
                    );
                    break;
                default:
                    break;
                }
            }

            return rules;
        },
        getValidationIcon(action) {
            if (!action || !action?.validation_type) {
                return '';
            }

            switch (action.validation_type) {
                case 'email_address':
                    return 'mdi-email';
                case 'phone_number':
                    return 'mdi-phone';
                case 'number':
                    return 'mdi-numeric';
                case 'vin':
                    return 'mdi-car-info';
                default:
                    return '';
            }
        },
        getActionLabel(dispositionAction) {
            if (!dispositionAction) {
                return '';
            }
            
            let label = '';

            switch (dispositionAction?.actionable_type) {
                case 'employee':
                    label = dispositionAction.is_multi_select ? 'Employees' : 'Employee';
                    break;
                case 'customer_vehicle': 
                    label = dispositionAction.is_multi_select ? 'Customer Vehicles' : 'Customer Vehicle';
                    break;
                case 'multiple_choice':
                    label = dispositionAction.is_multi_select ? 'Multiple Choice' : 'Multiple Choice';
                    break;
                case 'input':
                    if (dispositionAction.validation_type) {
                        label = `${titleize(dispositionAction.validation_type)}`;
                    } else {
                        label = 'Custom Input';
                    }
                    break;
                case 'textarea':
                    label = 'Custom Textarea';
                    break;
                default:
                    label = 'Disposition Action';
            }

            if (dispositionAction?.name) {
                label = dispositionAction.name;
            }

            return dispositionAction?.is_required ? `${label} *` : label;
        },
        init() {
            this.buildActionsWithTypes();
            if (this.renderEmployees) {
                this.getEmployees();
            }

            if (this.renderCustomerVehicle) {
                this.getCustomerVehicles();
            }
        },
        buildActionsWithTypes() {
            this.actionsWithEmployeeType = this.dispositionActions
                .filter(action => action.actionable_type === 'employee')
            
            this.actionsWithCustomerVehicleType = this.dispositionActions
                .filter(action => action.actionable_type === 'customer_vehicle')
            
            this.actionsWithInputType = this.dispositionActions
                .filter(action => action.actionable_type === 'input')
            
            this.actionsWithDispositionType = this.dispositionActions
                .filter(action => action.actionable_type === 'disposition')
            
            this.actionsWithTextareaType = this.dispositionActions
                .filter(action => action.actionable_type === 'textarea')
            
            this.actionsWithMultipleChoiceType = this.dispositionActions
                .filter(action => action.actionable_type === 'multiple_choice')
            
            this.buildActionForms();
        },
        buildActionForms() {
            // Set ensures reactivity since the values array is done via computed property
            if (this.renderCustomerVehicle) {
                for (const action of this.actionsWithCustomerVehicleType) {
                    const initialValue = action.is_multi_select ? [] : null;
                    this.$set(this.formCustomerVehicle, action.disposition_action_id, initialValue);
                }
            }

            if (this.renderEmployeeDropDown) {
                for (const action of this.actionsWithEmployeeType) {
                    const initialValue = action.is_multi_select ? [] : null;
                    this.$set(this.formEmployee, action.disposition_action_id, initialValue);
                }
            }
            
            if (this.renderInput) {
                for (const action of this.actionsWithInputType) {
                    this.$set(this.formInput, action.disposition_action_id, '');
                }
            }

            if (this.renderDispositions) {
                for (const action of this.actionsWithDispositionType) {
                    this.$set(this.formDispositions, action.disposition_action_id, null);
                }
            }

            if (this.renderTextarea) {
                for (const action of this.actionsWithTextareaType) {
                    this.$set(this.formTextarea, action.disposition_action_id, '');
                }
            }

            if (this.renderMultipleChoice) {
                for (const action of this.actionsWithMultipleChoiceType) {
                    const initialValue = action.is_multi_select ? [] : null;
                    this.$set(this.formMultipleChoice, action.disposition_action_id, initialValue);
                }
            }
        },
        getCustomerVehicles() {
            if (!this.currentCampaignCustomer.customer_id) {
                return;
            }

            const options = {
                account_id: this.currentCampaignCustomer.account_id,
                customer_id: this.currentCampaignCustomer.customer_id,
                organization_id: this.currentCampaignCustomer.organization_id,
            };

            GetCustomerVehicles(options).then((response) => {
                if (response?.Data?.customer_vehicles?.length > 0) {
                    this.customerVehicles = response.Data.customer_vehicles
                } else {
                    this.customerVehicles = [];
                }
            })
        },
        onCustomerVehicleCreated(newVehicle) {
            // Refresh the customer vehicles list when a new vehicle is created
            this.getCustomerVehicles();
        },
        onCustomerVehicleDeleted(customerVehicleId) {
            // Remove the deleted vehicle from our local list
            if (this.customerVehicles?.length > 0) {
                const idxToRemove = this.customerVehicles.findIndex(vehicle => vehicle.customer_vehicle_id === customerVehicleId);
                if (idxToRemove >= 0) {
                    this.customerVehicles.splice(idxToRemove, 1);
                }
            }
            
            // Also clear the form value if the deleted vehicle was selected
            if (this.isCustomerVehicleMultiSelect) {
                if (Array.isArray(this.formCustomerVehicle.value)) {
                    this.formCustomerVehicle.value = this.formCustomerVehicle.value.filter(id => id !== customerVehicleId);
                }
            } else {
                if (this.formCustomerVehicle.value === customerVehicleId) {
                    this.formCustomerVehicle.value = null;
                }
            }
        },
    },
    computed: {
        //belt and suspender, but already sorted from the back end
        sortedDispositionActions() {
            return [...this.dispositionActions].sort((a, b) => (a.action_order || 0) - (b.action_order || 0));
        },
        renderEmployeeDropDown() {
            return this.dispositionActions.some(action => action.actionable_type === 'employee');
        },
        renderInput() {
            return this.actionsWithInputType.length > 0;
        },
        renderTextarea() {
            return this.actionsWithTextareaType.length > 0;
        },
        renderCustomerVehicle() {
            return this.actionsWithCustomerVehicleType.length > 0;
        },
        renderEmployees() {
            return this.actionsWithEmployeeType.length > 0;
        },
        renderDispositions() {
            return this.actionsWithDispositionType.length > 0;
        },
        renderMultipleChoice() {
            return this.actionsWithMultipleChoiceType.length > 0;
        },
        isCustomerVehicleMultiSelect() {
            const vehicleAction = this.dispositionActions.find(action => action.actionable_type === 'customer_vehicle');
            return vehicleAction?.is_multi_select === true;
        },
        organizationId() {
            return this.currentCampaignCustomer?.organization_id;
        },
        customerVehiclesWithDescription() {
            return this.customerVehicles.map(vehicle => {
                const parts = [];
                
                // Add year if present
                if (vehicle.vehicle_model_year) {
                    parts.push(vehicle.vehicle_model_year);
                }
                
                // Add make if present
                if (vehicle.vehicle_make) {
                    parts.push(vehicle.vehicle_make);
                }
                
                // Add model if present
                if (vehicle.vehicle_model) {
                    parts.push(vehicle.vehicle_model);
                }
                
                // Add VIN if present
                if (vehicle.vin) {
                    parts.push(`<${vehicle.vin}>`);
                }
                
                return {
                    ...vehicle,
                    vehicle_description: parts.join(' ')
                };
            });
        },
        dispositionActionValues() {
            const values = [];
            
            // Customer vehicle fields
            if (this.renderCustomerVehicle) {
                for (const [dispositionActionId, vehicleValues] of Object.entries(this.formCustomerVehicle)) {
                    // Normalize to array
                    if (!vehicleValues) {
                        continue;
                    }
                    const normalizedValues = Array.isArray(vehicleValues) ? vehicleValues : [vehicleValues];
                    
                    normalizedValues.forEach(value => {
                        values.push({
                            disposition_action_id: parseInt(dispositionActionId),
                            actionable_id: value,
                            actionable_type: 'customer_vehicle',
                            value: null
                        });
                    });
                }
            }

            // Employee fields
            if (this.renderEmployeeDropDown) {
                for (const [dispositionActionId, employeeValues] of Object.entries(this.formEmployee)) {
                    // Normalize to array
                    if (!employeeValues) {
                        continue;
                    }
                    const normalizedValues = Array.isArray(employeeValues) ? employeeValues : [employeeValues];
                    
                    normalizedValues.forEach(value => {
                        values.push({
                            disposition_action_id: parseInt(dispositionActionId),
                            actionable_id: value,
                            actionable_type: 'employee',
                            value: null
                        });
                    });
                }
            }

            // Input fields
            // Because we validate the input inside this property, the disposition tab gets the benefit of
            // the validation without needing to emit or set any other values.
            if (this.renderInput) {
                for (const [dispositionActionId, inputValue] of Object.entries(this.formInput)) {
                    if (typeof inputValue === 'string' && inputValue.trim() !== '') {
                        // Only create value if the input is valid
                        const ref = this.$refs['actionInput' + dispositionActionId];
                        if (ref) {
                            // Handle both single ref and array of refs
                            const refElement = Array.isArray(ref) ? ref[0] : ref;
                            if (refElement) {
                                refElement.validate();
                                if (refElement.hasError) {
                                    continue;
                                }
                            }
                        }

                        values.push({
                            disposition_action_id: parseInt(dispositionActionId),
                            actionable_id: 0,
                            actionable_type: 'input',
                            value: inputValue
                        });
                    }
                }
            }

            // Disposition fields
            if (this.renderDispositions) {
                for (const [dispositionActionId, dispositionValue] of Object.entries(this.formDispositions)) {
                    if (dispositionValue) {
                        values.push({
                            disposition_action_id: parseInt(dispositionActionId),
                            actionable_id: dispositionValue,
                            actionable_type: 'disposition',
                            value: null
                        });
                    }
                }
            }

            // Textarea fields
            if (this.renderTextarea) {
                for (const [dispositionActionId, textareaValue] of Object.entries(this.formTextarea)) {
                    if (typeof textareaValue === 'string' && textareaValue.trim() !== '') {
                        values.push({
                            disposition_action_id: parseInt(dispositionActionId),
                            actionable_id: 0,
                            actionable_type: 'textarea',
                            value: textareaValue
                        });
                    }
                }
            }

            // Multiple choice fields
            if (this.renderMultipleChoice) {
                for (const [dispositionActionId, multipleChoiceValues] of Object.entries(this.formMultipleChoice)) {

                    // Normalize to array
                    if (!multipleChoiceValues) {
                        continue;
                    }
                    const normalizedValues = Array.isArray(multipleChoiceValues) ? multipleChoiceValues : [multipleChoiceValues];

                    normalizedValues.forEach(value => {
                            values.push({
                                disposition_action_id: parseInt(dispositionActionId),
                                actionable_id: value,
                                actionable_type: 'multiple_choice',
                            value: null
                        });
                    });
                }
            }

            return values;
        },
        filteredCampaignDispositions() {
            if (!this.dispositionActions || this.dispositionActions.length === 0) {
                return [];
            }

            const currentDispositionId = this.dispositionActions[0]?.disposition_id;
            return this.campaignDispositions.filter(disposition => disposition.disposition_id !== currentDispositionId);
        },
    },
    components: {
        AutocompleteDividedByType
    },
    props: {
        campaignId: Number,
        campaignDispositions: Array,
        currentCampaignCustomer: Object,
        dispositionActions: Array,
        setDispositionActionValuesValid: Function,
        validCustomer: Boolean,
        validDispositionActions: Boolean,
    },
    watch: {
        dispositionActionValues: {
            handler() {
                this.emitDispositionActionValues();
            },
            deep: true,
            immediate: true,
        },
        dispositionActions: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    // Reset all form data when disposition actions change
                    this.formEmployee = {};
                    this.formCustomerVehicle = {};
                    this.formInput = {};
                    this.formDispositions = {};
                    this.formTextarea = {};
                    
                    // Rebuild the forms with new actions
                    this.init();
                }
            },
            deep: true,
            immediate: false,
        },
    },
    created() {
        this.init();
    },
    mounted() {
        // Listen for customer vehicle events
        this.$root.$on('customer-vehicle-created', this.onCustomerVehicleCreated);
        this.$root.$on('customer-vehicle-deleted', this.onCustomerVehicleDeleted);
    },
    destroyed() {
        // Clean up event listeners
        this.$root.$off('customer-vehicle-created', this.onCustomerVehicleCreated);
        this.$root.$off('customer-vehicle-deleted', this.onCustomerVehicleDeleted);
    },
})
</script>

<style lang="scss" scoped>
    .textarea-container {
        position: relative;
    }

    .textarea-container .delete-btn {
        position: absolute;
        top: -4px;
        right: 8px;
        z-index: 1;
    }
</style>